import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Eye, EyeOff, Leaf } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
        transformRequest: [(data) => JSON.stringify(data)],
      });
      if (response.data?.token) {
        localStorage.setItem("admin_token", response.data.token);
        toast.success("Welcome back, Admin!");
        navigate("/admin/dashboard");
      } else {
        setError("Unexpected response from server. Please try again.");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Login failed. Please check your credentials.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center p-6" data-testid="admin-login-page">
      <Link to="/" className="absolute top-6 left-6 z-20 flex items-center gap-2 group" data-testid="home-button">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center group-hover:shadow-md transition-shadow">
          <Leaf className="w-4 h-4 text-white" />
        </div>
        <span className="font-serif text-lg font-medium text-[#2D3A2D]">My Story</span>
      </Link>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl p-10 shadow-xl">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#4A5D4A] flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#F8F6F1]" />
            </div>
          </div>
          <h1 className="font-serif text-2xl font-medium text-[#2D3A2D] text-center mb-1">Admin</h1>
          <p className="font-sans text-sm text-[#4A5D4A] text-center mb-8">My Story Dashboard</p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-sans" data-testid="admin-login-error">
                {error}
              </div>
            )}
            <div>
              <label className="font-sans text-xs text-[#4A5D4A] uppercase tracking-wider mb-1.5 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8] bg-[#F8F6F1] font-sans text-sm text-[#2D3A2D] outline-none focus:border-[#7A9B7E] focus:ring-1 focus:ring-[#7A9B7E] transition-all"
                placeholder="admin@mystory.app"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="font-sans text-xs text-[#4A5D4A] uppercase tracking-wider mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8] bg-[#F8F6F1] font-sans text-sm text-[#2D3A2D] outline-none focus:border-[#7A9B7E] focus:ring-1 focus:ring-[#7A9B7E] transition-all pr-10"
                  placeholder="Enter password"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A8A7A]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-3 rounded-full bg-[#4A5D4A] text-[#F8F6F1] font-sans font-medium hover:bg-[#2D3A2D] transition-all disabled:opacity-50 mt-2"
              data-testid="admin-login-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
